import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, Loader2 } from 'lucide-react';
import { aiTextService } from '@/lib/ai-text-service';
import {
  createRewriteRequestGuard,
  runGuardedRewriteRequest,
  type RewriteRequestGuard,
} from '@shared/rewrite-request-guard';
import { useToast } from '@/hooks/use-toast';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface AIRewriteButtonProps {
  /** Aktueller Textinhalt des Feldes, das umgeschrieben werden soll */
  text: string;
  /** Wird mit dem umgeschriebenen Text aufgerufen — direkt ins Feld schreiben */
  onRewritten: (rewrittenText: string) => void;
  /** Stabile Dokument-/Feld-ID, z.B. "quote:slot:41:vortext". */
  scopeKey: string;
  context?: {
    gewerk?: string;
  };
  disabled?: boolean;
  className?: string;
}

const QUICK_COMMANDS = [
  'VOB-konform anpassen',
  'Rechtschreibung & Grammatik korrigieren',
  'Förmlicher formulieren',
  'Kürzer fassen',
];

export function AIRewriteButton({
  text,
  onRewritten,
  scopeKey,
  context,
  disabled = false,
  className = '',
}: AIRewriteButtonProps) {
  const [open, setOpen] = useState(false);
  const [customInstruction, setCustomInstruction] = useState('');
  const [isRewriting, setIsRewriting] = useState(false);
  const { toast } = useToast();
  const rewriteGuardRef = useRef<RewriteRequestGuard | null>(null);
  const rewriteGuard = rewriteGuardRef.current ?? createRewriteRequestGuard(text, scopeKey);
  rewriteGuardRef.current = rewriteGuard;
  rewriteGuard.updateScope(scopeKey);
  rewriteGuard.updateSource(text);

  useEffect(() => {
    setIsRewriting(false);
    return () => {
      rewriteGuard.invalidate();
    };
  }, [rewriteGuard, scopeKey]);

  const runRewrite = async (instruction: string) => {
    if (!text || text.trim().length === 0) {
      toast({
        title: 'Kein Text vorhanden',
        description: 'Bitte geben Sie zuerst einen Text ein.',
        variant: 'destructive',
      });
      return;
    }

    if (!instruction || instruction.trim().length === 0) {
      toast({
        title: 'Keine Anweisung',
        description: 'Bitte wählen Sie einen Befehl oder geben Sie eine eigene Anweisung ein.',
        variant: 'destructive',
      });
      return;
    }

    setIsRewriting(true);
    const sourceText = text;

    try {
      await runGuardedRewriteRequest(
        rewriteGuard,
        (signal) => aiTextService.rewriteText(sourceText, instruction, context?.gewerk, signal),
        (result) => {
          if (!result.success) {
            toast({
              title: 'KI-Befehl fehlgeschlagen',
              description: result.error || 'Unbekannter Fehler bei der KI-Textüberarbeitung.',
              variant: 'destructive',
            });
            return;
          }

          if (!result.rewritten) return;

          onRewritten(result.rewritten);
          setOpen(false);
          setCustomInstruction('');

          if (result.safeguarded) {
            toast({
              title: 'Pflichtangaben geschützt',
              description: result.note || 'Pflichtangaben wurden geschützt/wiederhergestellt.',
            });
          } else {
            toast({
              title: 'Text umgeschrieben',
              description: 'Die KI hat den Text gemäß Ihrer Anweisung überarbeitet. Pflichttexte (z.B. GAEB-Vorgaben) bleiben geschützt.',
            });
          }
        },
        { onLatestSettled: () => setIsRewriting(false) },
      );
    } catch (error) {
      console.error('AI rewrite error:', error);
      toast({
        title: 'Fehler',
        description: 'Ein unerwarteter Fehler ist aufgetreten.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                type="button"
                onClick={() => setOpen(true)}
                disabled={disabled || !text || text.trim().length === 0}
                size="sm"
                variant="outline"
                className={`
                  bg-gradient-to-r from-purple-500 to-purple-600
                  hover:from-purple-600 hover:to-purple-700
                  text-white border-purple-500 hover:border-purple-600
                  ${className}
                `}
              >
                <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                KI
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p className="font-medium">KI-Befehle</p>
            <p className="text-sm text-gray-600">
              Schreibt den Text gemäß Ihrer Anweisung um (z.B. VOB-konform, Rechtschreibung).
              GAEB-Pflichttexte bleiben geschützt.
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <PopoverContent className="w-80" align="end">
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium">KI-Befehl auf diesen Text anwenden</p>
            <p className="text-xs text-muted-foreground">
              Der Text wird umgeschrieben; GAEB-Pflichttexte bleiben geschützt.
            </p>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {QUICK_COMMANDS.map((command) => (
              <Button
                key={command}
                type="button"
                size="sm"
                variant="secondary"
                disabled={isRewriting}
                onClick={() => runRewrite(command)}
                className="h-auto py-1 px-2 text-xs whitespace-normal text-left"
              >
                {command}
              </Button>
            ))}
          </div>

          <div className="space-y-1.5">
            <Textarea
              value={customInstruction}
              onChange={(e) => setCustomInstruction(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  runRewrite(customInstruction);
                }
              }}
              placeholder="Eigene Anweisung… (Enter zum Ausführen)"
              rows={2}
              disabled={isRewriting}
              className="text-sm"
            />
            <Button
              type="button"
              size="sm"
              className="w-full"
              disabled={isRewriting || customInstruction.trim().length === 0}
              onClick={() => runRewrite(customInstruction)}
            >
              {isRewriting ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  KI schreibt um…
                </>
              ) : (
                <>
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                  Anwenden
                </>
              )}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default AIRewriteButton;
